<template>
  <div class="assistant-widget">
    <!-- Docked panel, rising from the bubble -->
    <Transition name="assistant-panel">
      <section
        v-if="isOpen && !isExpanded"
        class="assistant-widget__panel"
        role="dialog"
        aria-label="CRM Assistant"
      >
        <AssistantWindow :expanded="false" @close="close" @toggle-expand="toggleExpanded" />
      </section>
    </Transition>

    <!-- Expanded: the same chat in a centered modal -->
    <Modal
      :show="isOpen && isExpanded"
      size="xl"
      dialog-max-width="1120px"
      :show-close-button="false"
      body-class="assistant-widget__modal-body"
      @close="close"
    >
      <div class="assistant-widget__modal-frame">
        <AssistantWindow :expanded="true" @close="close" @toggle-expand="toggleExpanded" />
      </div>
    </Modal>

    <!-- New AI reply drafts, announced as a message from the assistant -->
    <Transition name="assistant-notice">
      <div v-if="showNotice" class="assistant-widget__notice">
        <button
          type="button"
          class="assistant-widget__notice-body"
          :aria-label="`${noticeTitle}. Open in the CRM Assistant`"
          @click="openReplies"
        >
          <span class="assistant-widget__notice-avatars" aria-hidden="true">
            <ContactAvatar
              v-for="item in noticeItems.slice(0, 3)"
              :key="item.conversationId"
              class="assistant-widget__notice-avatar"
              :name="item.contact?.name ?? 'Contact'"
              :src="item.contact?.profilePictureUrl"
              :size="34"
            />
            <span class="assistant-widget__notice-spark">
              <Icon icon="mdi:creation" />
            </span>
          </span>
          <span class="assistant-widget__notice-text">
            <span class="assistant-widget__notice-from">
              CRM Assistant
              <span class="assistant-widget__notice-new">New</span>
            </span>
            <span class="assistant-widget__notice-title">{{ noticeTitle }}</span>
            <span class="assistant-widget__notice-sub">{{ noticeSubtitle }}</span>
          </span>
        </button>
        <button
          type="button"
          class="assistant-widget__notice-close"
          aria-label="Hide this notification"
          title="Hide"
          @click="dismissNotice"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
    </Transition>

    <!-- The bubble -->
    <button
      type="button"
      class="assistant-widget__bubble"
      :class="{
        'assistant-widget__bubble--open': isOpen,
        'assistant-widget__bubble--busy': isSending,
      }"
      :aria-expanded="isOpen"
      :aria-label="isOpen ? 'Close CRM Assistant' : 'Open CRM Assistant'"
      @click="toggle"
    >
      <span class="assistant-widget__ring" aria-hidden="true" />
      <Transition name="assistant-icon" mode="out-in">
        <Icon v-if="isOpen" key="close" icon="mdi:chevron-down" class="assistant-widget__icon" />
        <Icon v-else key="open" icon="mdi:creation" class="assistant-widget__icon" />
      </Transition>
      <span v-if="!isOpen && badgeCount" class="assistant-widget__count">
        {{ badgeCount > 9 ? "9+" : badgeCount }}
      </span>
      <span v-else-if="!isOpen && hasUnread" class="assistant-widget__unread" />
      <span v-if="!isOpen" class="assistant-widget__label">Ask CRM Assistant</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Icon, Modal } from "@/components/ui";
import type { AgentUnreadReplyDraft } from "@/dtos/Agent.dto";
import AssistantWindow from "@/components/agent/assistant/AssistantWindow.vue";
import ContactAvatar from "@/components/agent/assistant/ContactAvatar.vue";
import { provideAssistantChat } from "@/composables/agent/useAssistantChat";

// The CRM assistant, a bubble in the bottom-right corner of every signed-in page (App.vue).
// It opens as a panel docked to the bubble, or expanded into a centered modal; the rep's
// last choice is remembered. Its chat is owned here, so it carries on across pages.
const {
  messages,
  isSending,
  isLoadingThread,
  now,
  inbox,
  showHistory,
  isVisible,
  conversationId,
  open: openThread,
} = provideAssistantChat();

const EXPANDED_STORAGE_KEY = "assistant-widget-expanded";

const savedExpanded = (): boolean => {
  try {
    return localStorage.getItem(EXPANDED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const isOpen = ref(false);
const isExpanded = ref(savedExpanded());
// A reply landed while the window was closed.
const hasUnread = ref(false);

const open = () => {
  isOpen.value = true;
  hasUnread.value = false;
};
const close = () => (isOpen.value = false);
const toggle = () => (isOpen.value ? close() : open());

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, String(isExpanded.value));
  } catch {
    // Storage blocked: the choice still applies until the page reloads.
  }
};

watch(
  () => messages.value.length,
  (length, previous) => {
    const last = messages.value[length - 1];
    if (!isOpen.value && length > (previous ?? 0) && last?.role !== "user") hasUnread.value = true;
  }
);

// Drafts in the open chat still waiting on the rep, shown on the bubble.
const badgeCount = computed(
  () =>
    messages.value
      .flatMap((m) => m.actions ?? [])
      .filter(
        (a) =>
          a.status === "pending" && (!a.expiresAt || new Date(a.expiresAt).getTime() > now.value)
      ).length + inbox.unread.value.length
);

// ── New reply drafts ────────────────────────────────────────
// Unread AI reply drafts pop up above the bubble as a message from the assistant: one
// contact's reply, or "3 new replies". Hiding it keeps it hidden until another one comes.
const CHANNEL_LABEL = { email: "by email", linkedin: "on LinkedIn" } as const;
const noticeKey = (item: { conversationId: string; actionId: string }) =>
  `${item.conversationId}:${item.actionId}`;
const dismissed = ref(new Set<string>());

const noticeItems = computed(() =>
  inbox.unread.value.filter((item) => !dismissed.value.has(noticeKey(item)))
);
const showNotice = computed(() => !isOpen.value && noticeItems.value.length > 0);

const nameOf = (item: AgentUnreadReplyDraft) => item.contact?.name ?? "A contact";

const noticeTitle = computed(() => {
  const items = noticeItems.value;
  if (items.length !== 1) return `${items.length} new replies`;
  const [item] = items;
  return `${nameOf(item)} replied${item.channel ? ` ${CHANNEL_LABEL[item.channel]}` : ""}`;
});

const noticeSubtitle = computed(() => {
  const items = noticeItems.value;
  if (items.length === 1) {
    const { preview } = items[0];
    return preview ? `"${preview}" · I drafted a reply for you` : "I drafted a reply for you";
  }
  const names = items.slice(0, 2).map(nameOf);
  const others = items.length - names.length;
  return `${names.join(", ")}${others > 0 ? ` and ${others} more` : ""} · I drafted a reply to each`;
});

const dismissNotice = () => {
  dismissed.value = new Set([...dismissed.value, ...noticeItems.value.map(noticeKey)]);
};

/** One reply opens its conversation; several open the list, where each has its own. */
const openReplies = () => {
  const items = noticeItems.value;
  open();
  if (items.length === 1) {
    showHistory.value = false;
    // Re-read even when it's the thread already loaded: the new draft may have come since.
    openThread(items[0].conversationId, { reload: true });
  } else {
    showHistory.value = true;
  }
};

// Opening the window onto a reply thread that got a new draft while it was shut reads it.
// Skipped while a thread is loading (e.g. the notice just opened one): reloading the old
// thread here would cancel that load and land the rep in the wrong conversation.
watch(isOpen, (visible) => {
  isVisible.value = visible;
  const openId = conversationId.value;
  if (
    visible &&
    openId &&
    !isLoadingThread.value &&
    inbox.unread.value.some((item) => item.conversationId === openId)
  ) {
    openThread(openId, { reload: true });
  }
});

// The expanded modal renders in <body>, outside App.vue's v-show, so it would stay over the
// next page (or the dialer). Close it on navigation; the docked panel carries on.
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    if (isOpen.value && isExpanded.value) close();
  }
);
</script>

<style scoped>
.assistant-widget__bubble {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  box-shadow:
    0 14px 30px -10px rgb(99 102 241 / 70%),
    0 4px 10px rgb(17 24 39 / 12%),
    inset 0 1px 0 rgb(255 255 255 / 35%);
  color: #fff;
  cursor: pointer;
  transition:
    transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.2s ease;
}

.assistant-widget__bubble:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow:
    0 18px 36px -10px rgb(99 102 241 / 80%),
    0 6px 14px rgb(17 24 39 / 14%),
    inset 0 1px 0 rgb(255 255 255 / 35%);
}

.assistant-widget__bubble:active {
  transform: scale(0.96);
}

.assistant-widget__bubble:focus-visible {
  outline: 3px solid #c4b5fd;
  outline-offset: 3px;
}

.assistant-widget__icon {
  position: relative;
  font-size: 28px;
}

/* A soft halo that breathes while the bubble is closed. */
.assistant-widget__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgb(139 92 246 / 45%);
  animation: assistant-breathe 3s ease-out infinite;
  pointer-events: none;
}

.assistant-widget__bubble--open .assistant-widget__ring {
  animation: none;
}

.assistant-widget__bubble--busy .assistant-widget__ring {
  animation-duration: 1.2s;
}

.assistant-widget__count {
  position: absolute;
  top: -2px;
  right: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  background: #f43f5e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.assistant-widget__unread {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #22c55e;
}

/* "Ask CRM Assistant" slides out beside the bubble on hover. */
.assistant-widget__label {
  position: absolute;
  right: calc(100% + 12px);
  padding: 7px 12px;
  border-radius: 10px;
  background: #111827;
  box-shadow: 0 8px 20px -8px rgb(17 24 39 / 50%);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(6px);
  pointer-events: none;
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.assistant-widget__bubble:hover .assistant-widget__label,
.assistant-widget__bubble:focus-visible .assistant-widget__label {
  opacity: 1;
  transform: none;
}

/* ── New reply notice ─────────────────────────────────────── */
/* A chat bubble from the assistant, its tail pointing at the launcher below. */
.assistant-widget__notice {
  position: fixed;
  right: 24px;
  bottom: 96px;
  z-index: 9000;
  display: flex;
  align-items: flex-start;
  width: 340px;
  max-width: calc(100vw - 48px);
  border: 1px solid #ede9fe;
  border-radius: 18px 18px 4px 18px;
  background: #fff;
  box-shadow:
    0 24px 48px -18px rgb(76 29 149 / 35%),
    0 8px 18px -10px rgb(17 24 39 / 18%);
  transform-origin: bottom right;
}

.assistant-widget__notice-body {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 14px 4px 14px 14px;
  border: none;
  border-radius: inherit;
  background: none;
  text-align: left;
  cursor: pointer;
}

.assistant-widget__notice-body:focus-visible,
.assistant-widget__notice-close:focus-visible {
  outline: 2px solid #7c3aed;
  outline-offset: 2px;
}

.assistant-widget__notice-avatars {
  position: relative;
  display: flex;
  flex-shrink: 0;
  padding-right: 6px;
}

.assistant-widget__notice-avatar {
  border: 2px solid #fff;
}

.assistant-widget__notice-avatar + .assistant-widget__notice-avatar {
  margin-left: -14px;
}

/* The assistant's mark on the avatars: it's the assistant telling you. */
.assistant-widget__notice-spark {
  position: absolute;
  right: 0;
  bottom: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: #fff;
  font-size: 10px;
}

.assistant-widget__notice-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.assistant-widget__notice-from {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6d28d9;
  font-size: 12px;
  font-weight: 600;
}

.assistant-widget__notice-new {
  padding: 1px 6px;
  border-radius: 999px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.assistant-widget__notice-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.assistant-widget__notice-sub {
  display: -webkit-box;
  overflow: hidden;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.assistant-widget__notice-close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin: 8px 8px 0 0;
  border: none;
  border-radius: 8px;
  background: none;
  color: #9ca3af;
  font-size: 16px;
  cursor: pointer;
}

.assistant-widget__notice-close:hover {
  background: #f3f4f6;
  color: #4b5563;
}

.assistant-notice-enter-active,
.assistant-notice-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.28s cubic-bezier(0.34, 1.4, 0.64, 1);
}

.assistant-notice-enter-from,
.assistant-notice-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.92);
}

.assistant-widget__panel {
  position: fixed;
  right: 24px;
  bottom: 96px;
  z-index: 9000;
  display: flex;
  width: 420px;
  max-width: calc(100vw - 48px);
  height: min(680px, calc(100vh - 128px));
  overflow: hidden;
  border: 1px solid rgb(17 24 39 / 6%);
  border-radius: 20px;
  background: #fff;
  box-shadow:
    0 30px 60px -20px rgb(30 27 75 / 35%),
    0 12px 24px -12px rgb(17 24 39 / 18%);
  transform-origin: bottom right;
}

.assistant-widget__modal-frame {
  display: flex;
  height: min(780px, 86vh);
}

/* Panel and icon motion */
.assistant-panel-enter-active,
.assistant-panel-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.26s cubic-bezier(0.34, 1.3, 0.64, 1);
}

.assistant-panel-enter-from,
.assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.94);
}

.assistant-icon-enter-active,
.assistant-icon-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.assistant-icon-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.assistant-icon-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

@keyframes assistant-breathe {
  0% {
    box-shadow: 0 0 0 0 rgb(139 92 246 / 45%);
  }
  70%,
  100% {
    box-shadow: 0 0 0 14px rgb(139 92 246 / 0%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .assistant-widget__ring {
    animation: none;
  }

  .assistant-panel-enter-active,
  .assistant-panel-leave-active,
  .assistant-notice-enter-active,
  .assistant-notice-leave-active,
  .assistant-icon-enter-active,
  .assistant-icon-leave-active {
    transition: none;
  }
}
</style>

<style>
/* The modal body is rendered by <Modal> outside this component's scope. */
.assistant-widget__modal-body {
  overflow: hidden !important;
}
</style>
